
import * as THREE from "three";

const vertexShader = `
    ${THREE.ShaderChunk.common}
    ${THREE.ShaderChunk.lights_pars_begin}
    
    varying vec2 vUv;
    varying vec3 vViewPosition;
    varying vec3 vNormal;
    
    attribute vec4 tangent;

    void main() {
        ${THREE.ShaderChunk.uv_vertex}
        ${THREE.ShaderChunk.uv2_vertex}        
        ${THREE.ShaderChunk.beginnormal_vertex}
        ${THREE.ShaderChunk.defaultnormal_vertex}
        ${THREE.ShaderChunk.begin_vertex}
        
        vUv = uv;
        vNormal = normalize( transformedNormal );
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewPosition = - mvPosition.xyz;
        gl_Position = projectionMatrix * mvPosition;
    }
  `

const fragmentShader = `
    #extension GL_OES_standard_derivatives : enable
    uniform vec3 diffuse;
    uniform vec3 emissive;
    uniform vec3 specular;
    uniform float shininess;
    uniform float opacity;
    
    varying vec2 vUv;
    
    uniform vec2 viewportSize;
    uniform vec2 imageSize;
    uniform vec2 planeSize;
    uniform vec2 backgroundSize;
    uniform vec2 backgroundOffset;
    
    uniform vec2 imageDpi;
    uniform sampler2D map;
    uniform sampler2D normalMap;
    uniform sampler2D specularMap;
    
    uniform sampler2D maskSampler;
    uniform sampler2D lightingSampler;
    uniform float transparency;
    uniform float lightingOffset;
    uniform bool blendEdges;
    uniform bool isPBR;

    ${THREE.ShaderChunk.common}
    ${THREE.ShaderChunk.bsdfs}
    ${THREE.ShaderChunk.lights_pars_begin}
    ${THREE.ShaderChunk.lights_phong_pars_fragment}
    
    ${THREE.ShaderChunk.bumpmap_pars_fragment}
    ${THREE.ShaderChunk.normalmap_pars_fragment}
    ${THREE.ShaderChunk.specularmap_pars_fragment}
    
    vec3 perturbNormal2Arb( vec3 eye_pos, vec3 surf_norm, vec2 uv) {
    
        vec3 q0 = vec3( dFdx( eye_pos.x ), dFdx( eye_pos.y ), dFdx( eye_pos.z ) );
        vec3 q1 = vec3( dFdy( eye_pos.x ), dFdy( eye_pos.y ), dFdy( eye_pos.z ) );
        vec2 st0 = dFdx( uv.st );
        vec2 st1 = dFdy( uv.st );
        float scale = sign( st1.t * st0.s - st0.t * st1.s );
        vec3 S = normalize( ( q0 * st1.t - q1 * st0.t ) * scale );
        vec3 T = normalize( ( - q0 * st1.s + q1 * st0.s ) * scale );
        vec3 N = normalize( surf_norm );
        mat3 tsn = mat3( S, T, N );
        vec3 mapN = texture2D( normalMap, uv ).xyz * 2.0 - 1.0;
        
        mapN.xy *= ( float( gl_FrontFacing ) * 2.0 - 1.0 );
        return normalize( tsn * mapN );
    }

    const float inchesToMeter = 0.0254;    
    
    void main() {    
        // Crop (with scaling) so there are no bars

        float scale = min(backgroundSize.x / viewportSize.x, backgroundSize.y / viewportSize.y);
        vec2 newSize = backgroundSize / scale;
        vec2 offset = (newSize - viewportSize) / 2.0;
        vec2 relativeOffset = offset / newSize;

        vec2 screenCoord = vec2(
            backgroundOffset.x + relativeOffset.x + (1.0 - 2.0 * relativeOffset.x) * gl_FragCoord.x / viewportSize.x,
            backgroundOffset.y + relativeOffset.y + (1.0 - 2.0 * relativeOffset.y) * gl_FragCoord.y / viewportSize.y
        );

        vec3 lightingColor =  (lightingOffset + (1.0 - lightingOffset) * texture2D(lightingSampler, screenCoord).rgb) - 0.5;

        vec2 foregroundTexCoord = vUv * planeSize * imageDpi / (imageSize * inchesToMeter);

        vec3 diffuseColor = smoothstep(-0.0, 1.4, texture2D(map, foregroundTexCoord).rgb + lightingColor);
        
        if (isPBR) {
            float specularStrength = texture2D(specularMap, foregroundTexCoord).r;
            
            vec3 normal = normalize( vNormal );
            normal = perturbNormal2Arb( -vViewPosition, normal, foregroundTexCoord );
            
            BlinnPhongMaterial material;
            material.diffuseColor = 0.3 * diffuseColor;
            material.specularColor = specular;
            material.specularShininess = shininess;
            material.specularStrength = specularStrength;
            
            GeometricContext geometry;
            geometry.position = - vViewPosition;
            geometry.normal = normal;
            geometry.viewDir = normalize( vViewPosition );
            IncidentLight directLight;
            
            ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
            vec3 totalEmissiveRadiance = emissive;
            reflectedLight.directDiffuse = diffuseColor;
            DirectionalLight directionalLight;
            
            #pragma unroll_loop
            for ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {
                directionalLight = directionalLights[ i ];
                getDirectionalDirectLightIrradiance( directionalLight, geometry, directLight );
            
                RE_Direct( directLight, geometry, material, reflectedLight );
            }
            
            diffuseColor = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;
        }
        
        float mask = texture2D(maskSampler, screenCoord).r;

        // Make borderish pixels more transparent
        vec2 pixelSize = vec2(3.0/viewportSize.x, 3.0/viewportSize.y);
        
        if (blendEdges) {
            float borderness = 0.125 * (
                texture2D(maskSampler, screenCoord + vec2(-pixelSize.r, 0.0)).r +
                texture2D(maskSampler, screenCoord + vec2(pixelSize.r, 0.0)).r +
                texture2D(maskSampler, screenCoord + vec2(0.0, -pixelSize.g)).r +
                texture2D(maskSampler, screenCoord + vec2(0.0, pixelSize.g)).r +
                texture2D(maskSampler, screenCoord + vec2(pixelSize.r, pixelSize.g)).r +
                texture2D(maskSampler, screenCoord + vec2(-pixelSize.r, pixelSize.g)).r +
                texture2D(maskSampler, screenCoord + vec2(pixelSize.r, -pixelSize.g)).r +
                texture2D(maskSampler, screenCoord + vec2(-pixelSize.r, -pixelSize.g)).r);

            gl_FragColor = vec4(diffuseColor, mask * (1.0 - transparency) * borderness);
        } else {
            gl_FragColor = vec4(diffuseColor, mask * (1.0 - transparency));
        }
        
    }
`

export { vertexShader, fragmentShader }